/**
 * /tm-invoices/new — Build a new T&M invoice.
 *
 * Step 1: Pick project + rate sheet + period + optional PCO filter.
 * The API fetches eligible tickets in that window and builds the invoice.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Checkbox, FormControlLabel, MenuItem,
  TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { api } from "@/lib/api";
import {
  rateSheetsListQueryOptions,
  tmTicketsListQueryOptions,
  tmInvoiceKeys,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type { TMInvoice } from "@/lib/types";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";

export const Route = createFileRoute("/_auth/tm-invoices/new")({
  component: NewTMInvoicePage,
});

function NewTMInvoicePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [projectId, setProjectId] = useState("");
  const [rateSheetId, setRateSheetId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const periodEndBeforeStart = !!periodStart && !!periodEnd && periodEnd < periodStart;
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  // PCO filter: if empty array = no filter (all eligible)
  const [pcoFilter, setPcoFilter] = useState<Set<string>>(new Set());
  const [filterPcos, setFilterPcos] = useState(false);

  const sheetsQ = useQuery(rateSheetsListQueryOptions(
    projectId ? { project_id: projectId } : {}
  ));

  // Eligible tickets for PCO picker
  const eligibleQ = useQuery({
    ...tmTicketsListQueryOptions({ project_id: projectId || undefined }),
    enabled: !!projectId && filterPcos,
  });
  const eligiblePcos = useMemo(() => {
    const s = new Set<string>();
    (eligibleQ.data?.items ?? [])
      .filter((t) => t.status === "eligible")
      .forEach((t) => s.add(t.pco_number));
    return [...s].sort();
  }, [eligibleQ.data?.items]);

  const build = useMutation({
    mutationFn: () =>
      api<TMInvoice>("/api/money/tm-invoices", {
        method: "POST",
        body: {
          project_id: projectId,
          rate_sheet_id: rateSheetId,
          period_start: periodStart,
          period_end: periodEnd,
          pco_numbers: filterPcos && pcoFilter.size > 0 ? [...pcoFilter] : undefined,
          invoice_number: invoiceNumber || undefined,
          notes: notes || undefined,
        },
      }),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: tmInvoiceKeys.all });
      toast.success("Invoice created");
      navigate({ to: "/tm-invoices/$invoiceId", params: { invoiceId: inv.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit =
    projectId && rateSheetId && periodStart && periodEnd && !periodEndBeforeStart && !build.isPending;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate({ to: "/tm-invoices" })}
        >
          All Invoices
        </Button>
        <Typography variant="h5">New T&amp;M Invoice</Typography>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4 dark:bg-gray-900 dark:border-gray-700">
        {/* Project */}
        <ProjectIdSelect
          fullWidth
          size="small"
          label="Project *"
          value={projectId}
          onChange={(id) => {
            setProjectId(id);
            setRateSheetId("");
            setPcoFilter(new Set());
          }}
          statusFilter={STANDARD_PROJECT_STATUSES}
          required
          placeholder="Search project code or name…"
        />

        {/* Rate Sheet */}
        <TextField
          select
          fullWidth
          size="small"
          label="Rate Sheet *"
          value={rateSheetId}
          onChange={(e) => setRateSheetId(e.target.value)}
          disabled={!projectId}
        >
          <MenuItem value="">— select —</MenuItem>
          {(sheetsQ.data?.items ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name} ({s.effective_from} – {s.effective_to ?? "open"})
            </MenuItem>
          ))}
        </TextField>

        {/* Period */}
        <div className="grid grid-cols-2 gap-4">
          <TextField
            size="small"
            label="Period Start *"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="First day of the billing period (e.g. 2026-06-01)"
          />
          <TextField
            size="small"
            label="Period End *"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={periodEndBeforeStart}
            helperText={periodEndBeforeStart ? "End date must be on or after start date" : "Last day of the billing period"}
          />
        </div>

        {/* Invoice # */}
        <TextField
          fullWidth
          size="small"
          label="Invoice Number (optional)"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          placeholder="e.g. INV-2026-001"
        />

        {/* Notes */}
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {/* PCO filter */}
        <div>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filterPcos}
                disabled={!projectId}
                onChange={(e) => {
                  setFilterPcos(e.target.checked);
                  if (!e.target.checked) setPcoFilter(new Set());
                }}
              />
            }
            label="Filter by specific PCO numbers"
          />
          {filterPcos && (
            <div className="mt-2 ml-6 space-y-1">
              {eligibleQ.isLoading ? (
                <span className="text-sm text-slate-400">Loading PCOs…</span>
              ) : eligiblePcos.length === 0 ? (
                <span className="text-sm text-slate-400">No eligible PCOs found.</span>
              ) : (
                eligiblePcos.map((pco) => (
                  <FormControlLabel
                    key={pco}
                    control={
                      <Checkbox
                        size="small"
                        checked={pcoFilter.has(pco)}
                        onChange={(e) => {
                          const next = new Set(pcoFilter);
                          if (e.target.checked) next.add(pco);
                          else next.delete(pco);
                          setPcoFilter(next);
                        }}
                      />
                    }
                    label={pco}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outlined" onClick={() => navigate({ to: "/tm-invoices" })}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => build.mutate()}
        >
          {build.isPending ? "Building…" : "Create Draft Invoice"}
        </Button>
      </div>
    </div>
  );
}
